import { useQuery } from '@tanstack/react-query';
import { Link, useNavigate, useParams, useSearch } from '@tanstack/react-router';
import { adminQueries } from '@blimo/api-client';
import { useApi, useMe } from '@blimo/api-client/react';
import { formatDate, hasPermission, TENANCY_PARTY_ROLE_LABELS } from '@blimo/shared';
import {
  Badge,
  Button,
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  DescriptionList,
  ErrorState,
  PageHeader,
  StatusBadge,
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from '@blimo/ui';
import { DetailSkeleton, PartyList, PropertyLink, UnitLabel } from '../../components/data.jsx';
import { DocumentManager } from '../../components/documents/document-manager.jsx';

/** Tabs des Mietverhältnisses; `dokumente` nur mit `documents.view`. */
const TENANCY_TABS = [
  { id: 'uebersicht', label: 'Übersicht' },
  { id: 'dokumente', label: 'Dokumente', permission: 'documents.view' },
];

/**
 * Verwaltung › Mietverhältnisse › Detail (read-only – Quelle Buchhaltung/WinCasa) mit Dokumentablage je Mietverhältnis
 * (F-409, `?tab=dokumente`). Mietverhältnis-Ordner sind intern, außer sie werden ausdrücklich im Portal sichtbar geschaltet.
 */
export function TenancyDetailPage() {
  const api = useApi();
  const { data: me } = useMe();
  const { tenancyId } = useParams({ strict: false });
  const search = useSearch({ strict: false });
  const navigate = useNavigate();
  const { data, error, isPending, refetch } = useQuery(adminQueries.tenancy(api, tenancyId));
  const breadcrumb = (
    <Link to="/mietverhaeltnisse" className="hover:underline">
      Mietverhältnisse
    </Link>
  );

  if (error) {
    return (
      <>
        <PageHeader title="Mietverhältnis" breadcrumb={breadcrumb} />
        <ErrorState
          error={error}
          onRetry={() => refetch()}
          notFoundTitle="Mietverhältnis nicht gefunden"
          action={
            <Button asChild variant="outline" size="sm">
              <Link to="/mietverhaeltnisse">Zur Liste</Link>
            </Button>
          }
        />
      </>
    );
  }
  if (isPending) return <DetailSkeleton />;

  const tenancy = data.data;
  const property = data.meta?.property;
  const visibleTabs = TENANCY_TABS.filter((t) => !t.permission || hasPermission(me, t.permission));
  const tab = visibleTabs.some((t) => t.id === search.tab) ? search.tab : 'uebersicht';
  const setTab = (next) =>
    navigate({ to: '/mietverhaeltnisse/$tenancyId', params: { tenancyId }, search: { tab: next === 'uebersicht' ? undefined : next }, replace: true });

  return (
    <>
      <PageHeader
        breadcrumb={breadcrumb}
        title={`Mietverhältnis ${tenancy.account_number ?? `#${tenancy.id}`}`}
        description={
          <span className="flex flex-wrap items-center gap-2">
            {property && <PropertyLink property={property} />}
            <UnitLabel unit={tenancy.unit} />
            <StatusBadge domain="tenancy" status={tenancy.status} />
            {tenancy.vat_reporting && <Badge variant="secondary">USt-Ausweis</Badge>}
          </span>
        }
      />
      <Tabs value={tab} onValueChange={setTab}>
        <TabsList aria-label="Bereiche des Mietverhältnisses">
          {visibleTabs.map((t) => (
            <TabsTrigger key={t.id} value={t.id}>
              {t.label}
            </TabsTrigger>
          ))}
        </TabsList>
        <TabsContent value="uebersicht">
          <div className="grid gap-3 lg:grid-cols-2">
            <Card>
              <CardHeader>
                <CardTitle as="h2">Vertrag</CardTitle>
                <CardDescription>Quelle: Buchhaltung (nur lesend)</CardDescription>
              </CardHeader>
              <CardContent>
                <DescriptionList
                  items={[
                    { label: 'Mieterkonto', value: tenancy.account_number },
                    { label: 'Vertragsabschluss', value: tenancy.contract_signed_on ? formatDate(tenancy.contract_signed_on) : null },
                    { label: 'Beginn', value: tenancy.starts_on ? formatDate(tenancy.starts_on) : null },
                    { label: 'Ende', value: tenancy.ends_on ? formatDate(tenancy.ends_on) : null },
                    { label: 'Gekündigt am', value: tenancy.terminated_on ? formatDate(tenancy.terminated_on) : null },
                  ]}
                />
              </CardContent>
            </Card>
            <Card>
              <CardHeader>
                <CardTitle as="h2">Mieter</CardTitle>
              </CardHeader>
              <CardContent>
                <PartyList parties={tenancy.parties} roleLabels={TENANCY_PARTY_ROLE_LABELS} />
              </CardContent>
            </Card>
          </div>
        </TabsContent>
        <TabsContent value="dokumente">
          <DocumentManager ownerType="tenancy" ownerId={tenancyId} title="Dokumente des Mietverhältnisses" />
        </TabsContent>
      </Tabs>
    </>
  );
}
